/**
 * AI changes (metaplan A10; AIE §10 "Dashboard"; client-MCP plan §10.1): the **approvals inbox** of pending MCP
 * changes across kinds — on a production tenant an MCP `apply` / `revert` waits for a person unless it is auto-applied
 * or self-approved; real-booking renders and a human-only Apply live here, linked from the MCP tool output and the
 * approver e-mails — and the change sets AI editing, Design Imports and auto-translation made, with their revert.
 *
 * Routes (NotificationsPage.tsx; the paths the backend links to, `demo/lib/ai_edit/links.py`): /notifications/ai_changes
 * (tabs Approvals — the landing tab, the inbox the e-mails link to — and Change sets, `?tab=change_sets`; the older
 * `?tab=pending` is the inbox), /notifications/ai_changes/:id, /notifications/ai_changes/proposals/:id. The menu shows
 * it as a top-level entry (layout/menu.js).
 *
 * Permissions are explicit-only (notification.mcp_list / _read / _write / _approve; Full access does not imply them)
 * and are decided by the backend: every page first reads `GET ai_changes/permissions/`. `list` opens the inbox only
 * (`mcp_list` holders see the listing and nothing behind it); `read` opens the change sets, a change and a proposal.
 * A permission counts on the operator it was given for (review W3 M1): those booleans say "on some operator", and
 * what one change allows comes with it (a row's `can_approve`, a change set's `permissions`) or from the backend's
 * answer on its operator (a 404, renders' `all_tags_required`), worded per company when the inbox spans several.
 * Every call keeps the session on 403 (./api.js).
 *
 * Content kinds (client-MCP plan §4.6 stage 5, §4.7 "Batches", Q1; backend P3-16): a content change's card and review
 * show its records from its self-describing summary (./ReviewSections.js); a content apply records one change set per
 * record in one batch, so the Change sets tab lists a batch (`?batch_id=`), each change set links its batch and
 * reverts that record only. There is no batch revert here: a batch's revert is asked over MCP and approved in the
 * inbox, its card listing the records it reverts.
 */
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom';

import { DataAPIContext } from '../../api';
import PageMeta from '../../components/PageMeta';
import * as api from './api';
import {
    actorLabel,
    AIPermissionsContext,
    BASE,
    batchHref,
    canApprove,
    ChangeSetLink,
    changeSetClient,
    CHANGE_REASON_LABEL,
    channelLabel,
    ClientName,
    EntryDiff,
    fmtTs,
    notFoundText,
    PendingChangeCard,
    ProposalItems,
    ReasonText,
    RevertDialog,
    StatusChip,
    useAIPermissions,
} from './parts';
import { FranchiseCopySummary, franchiseNotShown, PreviewSummary } from './PreviewSummary';

// ---- permissions ---------------------------------------------------------------------------------------------
// The booleans say "on some operator the viewer reaches" (review W3 M1): they open a page; what one change allows
// comes with it (`can_approve`, a change set's `permissions`) or from the backend's answer (./parts.js).

export { useAIPermissions };

/** The approvals inbox: `list` (an older backend without it: `read`). */
const mayList = (perms) => !!(perms && (perms.list ?? perms.read));

const GRANTS = '“Notification: AI editing over MCP — list”, “— read”, “— propose, apply, revert” or “— approve pending changes”';

/**
 * Loads the viewer's AI-editing permissions; renders children only to a viewer who may see them: `need="list"` for
 * the inbox, `need="read"` for a change set or a proposal.
 */
export const AIChangesGate = ({ children, need = 'read' }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [state, setState] = React.useState({ loading: true, perms: null, error: null });
    React.useEffect(() => {
        let alive = true;
        api.getPermissions(dataAPI)
            .then((perms) => alive && setState({ loading: false, perms, error: null }))
            .catch((error) => alive && setState({ loading: false, perms: null, error }));
        return () => {
            alive = false;
        };
    }, [dataAPI]);
    if (state.loading) {
        return (
            <Box p={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }
    if (state.error) {
        return (
            <Box p={2}>
                <Alert severity="error">Could not load your AI editing permissions: {state.error.message}</Alert>
            </Box>
        );
    }
    if (!mayList(state.perms)) {
        return (
            <Box p={2}>
                <PageMeta title="AI changes" />
                <Alert severity="info">
                    AI changes and their approvals are visible to people granted {GRANTS} in an access group, or an AI
                    vocabulary permission (its changes only). Full access does not include them: ask an administrator to
                    grant one explicitly.
                </Alert>
            </Box>
        );
    }
    if (need === 'read' && !state.perms.read) {
        return (
            <Box p={2}>
                <PageMeta title="AI changes" />
                <Alert severity="info">
                    Opening a change needs “Notification: AI editing over MCP — read” (or “— propose, apply, revert”, or
                    “— approve pending changes”). Your access lists the changes waiting in{' '}
                    <RouterLink to={BASE}>Approvals</RouterLink> only.
                </Alert>
            </Box>
        );
    }
    return <AIPermissionsContext.Provider value={state.perms}>{children}</AIPermissionsContext.Provider>;
};

const Disabled = () => {
    const perms = useAIPermissions();
    if (perms?.change_sets_enabled !== false) return null;
    return (
        <Alert severity="info" sx={{ mb: 2 }}>
            AI changes are not enabled on this server yet: nothing can be applied or reverted.
        </Alert>
    );
};

// ---- the list page ---------------------------------------------------------------------------------------------

const STATUSES = ['applied', 'partially_reverted', 'reverted', 'failed', 'applying', 'reverting'];
const CHANNELS = ['mcp', 'chat', 'design_import', 'translation_sync', 'revert'];

/** `batch`: one content apply's change sets only (`?batch_id=`, from a change set or a batch revert's request). */
const ChangeSetsTab = ({ batch = null }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [status, setStatus] = React.useState('');
    const [channel, setChannel] = React.useState('');
    const [state, setState] = React.useState({ loading: true, rows: [], count: 0, error: null });

    React.useEffect(() => {
        let alive = true;
        setState((prev) => ({ ...prev, loading: true, error: null }));
        api.listChangeSets(dataAPI, {
            page: page + 1, pageSize, status: status || undefined, channel: channel || undefined, batchId: batch || undefined,
        })
            .then((data) => alive && setState({ loading: false, rows: data?.results || [], count: data?.count ?? 0, error: null }))
            .catch((error) => alive && setState({ loading: false, rows: [], count: 0, error }));
        return () => {
            alive = false;
        };
    }, [dataAPI, page, pageSize, status, channel, batch]);

    return (
        <Box>
            {batch && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {'The change sets of one batch: a content change records one change set per record, and each reverts '
                        + 'that record only, on its own page. '}
                    <RouterLink to={`${BASE}?tab=${CHANGE_SETS_TAB}`}>Show every change set</RouterLink>
                </Alert>
            )}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="ai-status">Status</InputLabel>
                    <Select labelId="ai-status" label="Status" value={status} onChange={(ev) => { setStatus(ev.target.value); setPage(0); }}>
                        <MenuItem value="">Any</MenuItem>
                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="ai-channel">Made by</InputLabel>
                    <Select labelId="ai-channel" label="Made by" value={channel} onChange={(ev) => { setChannel(ev.target.value); setPage(0); }}>
                        <MenuItem value="">Any</MenuItem>
                        {CHANNELS.map((c) => <MenuItem key={c} value={c}>{channelLabel(c)}</MenuItem>)}
                    </Select>
                </FormControl>
                {state.loading && <CircularProgress size={20} />}
            </Stack>
            {state.error && <Alert severity="error">Could not load the changes: {state.error.message}</Alert>}
            {!state.loading && !state.error && state.rows.length === 0 && (
                <Typography variant="body2" color="text.secondary">No AI changes yet.</Typography>
            )}
            {state.rows.length > 0 && (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="AI changes">
                        <TableHead>
                            <TableRow>
                                <TableCell>When</TableCell>
                                <TableCell>Made by</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Who</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell align="right">Records</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.rows.map((row) => (
                                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => history.push(`${BASE}/${row.id}`)}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTs(row.created_ts)}</TableCell>
                                    <TableCell>
                                        {channelLabel(row.channel)}
                                        {row.client ? (
                                            <Typography variant="caption" color="text.secondary" component="div">
                                                <ClientName {...changeSetClient(row.client)} />
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <StatusChip status={row.status} />
                                            {row.has_conflicts && <Chip size="small" color="warning" variant="outlined" label="Edited since" />}
                                            {row.batch_id && !batch && (
                                                <Typography variant="caption" sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>
                                                    {/* the row opens the change set (one record); this link, its batch */}
                                                    <RouterLink to={batchHref(row.batch_id)} onClick={(ev) => ev.stopPropagation()}>Batch</RouterLink>
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{actorLabel(row.actor, row)}</TableCell>
                                    <TableCell sx={{ maxWidth: 420 }}>
                                        <Typography variant="body2" noWrap title={row.reason}>{row.reason || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap component="div" title={row.summary}>{row.summary}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{row.entry_count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={state.count}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[25, 50, 100]}
                        onPageChange={(_ev, p) => setPage(p)}
                        onRowsPerPageChange={(ev) => { setPageSize(parseInt(ev.target.value, 10)); setPage(0); }}
                    />
                </Paper>
            )}
        </Box>
    );
};

const PENDING_FILTERS = { open: 'pending,applying', all: '' };

// An apply's proposal review; a revert's change set, or a batch's change sets (a batch revert has no change set).
const pendingTarget = (p) => {
    if (p.kind === 'apply') return p.proposal_id ? `${BASE}/proposals/${p.proposal_id}` : null;
    if (p.change_set_id) return `${BASE}/${p.change_set_id}`;
    return p.batch_id ? batchHref(p.batch_id) : null;
};

const reviewWords = (p) => {
    if (p.kind === 'apply') return 'Review the proposed change';
    return p.change_set_id ? 'Review the change to revert' : 'Review the changes to revert';
};

/** Its review opens for the viewer: the row's `can_open` (a read grant on its operator, ORCH W4 b); an older backend
 *  without it: `read` on some operator, or a row the viewer may approve. */
const opensReview = (p, perms) => (typeof p.can_open === 'boolean' ? p.can_open : !!(perms?.read || canApprove(p)));

/** The approvals inbox: tenant users' pending MCP changes of every kind (client-MCP plan §10.1, ORCH-22). */
const ApprovalsTab = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const perms = useAIPermissions();
    const [filter, setFilter] = React.useState('open');
    const [state, setState] = React.useState({ loading: true, rows: [], error: null });
    const [reload, setReload] = React.useState(0);
    React.useEffect(() => {
        let alive = true;
        setState((prev) => ({ ...prev, loading: true, error: null }));
        api.listPending(dataAPI, { status: PENDING_FILTERS[filter] || undefined })
            .then((data) => alive && setState({ loading: false, rows: data?.results || [], error: null }))
            .catch((error) => alive && setState({ loading: false, rows: [], error }));
        return () => {
            alive = false;
        };
    }, [dataAPI, filter, reload]);
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                On a production account, a change made over MCP waits here until someone allowed to approve it applies it,
                unless auto-apply is on or its author may approve all of it.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label="Waiting" color={filter === 'open' ? 'primary' : 'default'} onClick={() => setFilter('open')} />
                <Chip label="All" color={filter === 'all' ? 'primary' : 'default'} onClick={() => setFilter('all')} />
                {state.loading && <CircularProgress size={20} />}
            </Stack>
            {state.error && <Alert severity="error">Could not load the requests: {state.error.message}</Alert>}
            {!state.loading && !state.error && state.rows.length === 0 && (
                <Typography variant="body2" color="text.secondary">Nothing is waiting.</Typography>
            )}
            {state.rows.map((p) => (
                <Box key={p.id}>
                    {opensReview(p, perms) && pendingTarget(p) && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <RouterLink to={pendingTarget(p)}>{reviewWords(p)}</RouterLink>
                        </Typography>
                    )}
                    <PendingChangeCard pending={p} onDecided={() => setReload((n) => n + 1)} />
                </Box>
            ))}
        </Box>
    );
};

const CHANGE_SETS_TAB = 'change_sets';

const AIChangesBody = () => {
    const location = useLocation();
    const history = useHistory();
    const perms = useAIPermissions();
    // The inbox is the landing tab (the approver e-mails link to BASE); the change sets need `read`.
    const query = new URLSearchParams(location.search);
    const changeSets = perms.read && query.get('tab') === CHANGE_SETS_TAB;
    const batch = query.get('batch_id') || null;
    return (
        <Box p={2}>
            <PageMeta title="AI changes" />
            <Typography variant="h6">AI changes</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changes made over MCP that wait for approval, and every change AI editing, design imports and
                auto-translation made, each one revertible.
            </Typography>
            <Disabled />
            {perms.read && (
                <Tabs
                    value={changeSets ? 1 : 0}
                    onChange={(_ev, v) => history.push(v === 1 ? `${BASE}?tab=${CHANGE_SETS_TAB}` : BASE)}
                    sx={{ mb: 2 }}
                >
                    <Tab label="Approvals" />
                    <Tab label="Change sets" />
                </Tabs>
            )}
            {/* keyed by the batch: another batch (or none) starts on the first page */}
            {changeSets ? <ChangeSetsTab key={batch || 'all'} batch={batch} /> : <ApprovalsTab />}
        </Box>
    );
};

export const AIChangesPage = () => (
    <AIChangesGate need="list">
        <AIChangesBody />
    </AIChangesGate>
);

// ---- one change set -------------------------------------------------------------------------------------------

const Info = ({ label, children }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
        <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{children || '—'}</Typography>
    </Grid>
);

const EntryRow = ({ changeSetId, entry, canView }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [open, setOpen] = React.useState(false);
    const [diff, setDiff] = React.useState(null);
    const [error, setError] = React.useState(null);
    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !diff && !error) {
            api.getEntryDiff(dataAPI, changeSetId, entry.seq)
                .then(setDiff)
                .catch((e) => setError(e.status === 503 ? 'The change-set store could not be read; try again later.' : e.message));
        }
    };
    return (
        <>
            <TableRow>
                <TableCell>{entry.seq}</TableCell>
                <TableCell>{entry.op}</TableCell>
                <TableCell>
                    {entry.display}
                    <Typography variant="caption" color="text.secondary" component="div">{entry.model} #{entry.object_pk}</Typography>
                </TableCell>
                <TableCell>{entry.revert_status || ''}</TableCell>
                <TableCell align="right">
                    {canView && (
                        <Button size="small" onClick={toggle} aria-expanded={open}>
                            {open ? 'Hide changes' : 'Show changes'}
                        </Button>
                    )}
                </TableCell>
            </TableRow>
            {canView && (
                <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} unmountOnExit>
                            <Box sx={{ py: 1 }}>
                                {!diff && !error && <CircularProgress size={18} />}
                                {error && <Alert severity="error">{error}</Alert>}
                                {diff && <EntryDiff diff={diff} />}
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

const ChangeSetBody = ({ id }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const viewer = useAIPermissions();
    const [state, setState] = React.useState({ loading: true, cs: null, error: null });
    const [entries, setEntries] = React.useState(null);
    const [historyRows, setHistoryRows] = React.useState(null);
    const [revertOpen, setRevertOpen] = React.useState(false);
    const [reload, setReload] = React.useState(0);

    React.useEffect(() => {
        let alive = true;
        setState((prev) => ({ ...prev, loading: true }));
        api.getChangeSet(dataAPI, id)
            .then((cs) => alive && setState({ loading: false, cs, error: null }))
            .catch((error) => alive && setState({ loading: false, cs: null, error }));
        api.listEntries(dataAPI, id).then((d) => alive && setEntries(d?.results || [])).catch(() => alive && setEntries([]));
        api.getHistory(dataAPI, id).then((d) => alive && setHistoryRows(d)).catch(() => alive && setHistoryRows({ results: [], truncated: false }));
        return () => {
            alive = false;
        };
    }, [dataAPI, id, reload]);

    if (state.loading && !state.cs) return <CircularProgress size={24} />;
    if (state.error) {
        return <Alert severity={state.error.status === 404 ? 'warning' : 'error'}>{state.error.status === 404 ? notFoundText('change', viewer) : state.error.message}</Alert>;
    }
    const cs = state.cs;
    const perms = cs.permissions || {};
    return (
        <Box>
            <PageMeta title={`AI change ${String(cs.id).slice(0, 8)}`} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="h6">{channelLabel(cs.channel)} change</Typography>
                <StatusChip status={cs.status} />
                {cs.has_conflicts && <Chip size="small" color="warning" variant="outlined" label="Edited since" />}
                <Box sx={{ flex: 1 }} />
                {perms.revert && cs.revertable && (
                    <Button variant="outlined" color="error" onClick={() => setRevertOpen(true)} disabled={cs.enabled === false}>
                        {cs.batch_id ? 'Revert this record…' : 'Revert…'}
                    </Button>
                )}
            </Stack>
            {cs.batch_id && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    One record of a batch: its revert undoes this record only.
                </Typography>
            )}
            {!cs.revertable && cs.revert_refusal && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>{cs.revert_refusal}</Typography>
            )}
            <ReasonText text={cs.reason} label={CHANGE_REASON_LABEL} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{cs.summary}</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Info label="Who">{actorLabel(cs.actor, cs)}</Info>
                <Info label="Client">
                    {cs.client ? <ClientName {...changeSetClient(cs.client)} /> : cs.credential_kind === 'dashboard_session' ? 'Dashboard' : null}
                </Info>
                <Info label="Created">{fmtTs(cs.created_ts)}</Info>
                <Info label="Applied">{fmtTs(cs.applied_ts)}</Info>
                <Info label="Request IP">{cs.request_ip}</Info>
                <Info label="Sign-in IP">{cs.login_ip}</Info>
                <Info label="Records">{`${cs.ops?.create ?? 0} created, ${cs.ops?.update ?? 0} updated, ${cs.ops?.delete ?? 0} deleted`}</Info>
                {cs.batch_id && (
                    <Info label="Batch"><RouterLink to={batchHref(cs.batch_id)}>All records of this batch</RouterLink></Info>
                )}
                {cs.original && <Info label="Reverts"><ChangeSetLink id={cs.original.id} /></Info>}
                {cs.reverted_by && <Info label="Reverted by"><ChangeSetLink id={cs.reverted_by.id} /></Info>}
                {cs.proposal_id && cs.channel === 'mcp' && (
                    <Info label="Proposal"><RouterLink to={`${BASE}/proposals/${cs.proposal_id}`}>#{cs.proposal_id}</RouterLink></Info>
                )}
                {cs.error && <Info label="Error">{cs.error}</Info>}
            </Grid>
            {cs.from_pending_change && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Applied by {actorLabel(cs.from_pending_change.decided_by)} from a request of {actorLabel(cs.from_pending_change.requested_by, cs.from_pending_change)}.
                </Alert>
            )}
            {(cs.pending_reverts || []).length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Revert requests</Typography>
                    {cs.pending_reverts.map((p) => (
                        <PendingChangeCard key={p.id} pending={p} onDecided={() => setReload((n) => n + 1)} />
                    ))}
                </Box>
            )}
            {cs.proposal && <ProposalItems title="What was asked" items={cs.proposal.items} />}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">Records</Typography>
            {!perms.view_content && (
                <Typography variant="caption" color="text.secondary" component="div">
                    Before → after needs the AI editing permission on all tags.
                </Typography>
            )}
            {entries === null ? (
                <CircularProgress size={20} />
            ) : (
                <Table size="small" aria-label="Records">
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Change</TableCell>
                            <TableCell>Record</TableCell>
                            <TableCell>Revert</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map((e) => (
                            <EntryRow key={e.seq} changeSetId={cs.id} entry={e} canView={!!perms.view_content} />
                        ))}
                    </TableBody>
                </Table>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">Change history it wrote</Typography>
            {historyRows === null ? (
                <CircularProgress size={20} />
            ) : (historyRows.results || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No history rows.</Typography>
            ) : (
                <Table size="small" aria-label="History">
                    <TableBody>
                        {historyRows.results.map((h) => (
                            <TableRow key={`${h.history}-${h.id}`}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTs(h.created_ts)}</TableCell>
                                <TableCell>{h.action}</TableCell>
                                <TableCell>{h.object_repr || `${h.content_type} #${h.object_pk}`}</TableCell>
                                <TableCell>{(h.changed_paths || []).join(', ')}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {historyRows?.truncated && <Typography variant="caption">Only the first 500 rows are shown.</Typography>}
            {revertOpen && (
                <RevertDialog
                    changeSetId={cs.id}
                    open={revertOpen}
                    onClose={() => setRevertOpen(false)}
                    onDone={() => setReload((n) => n + 1)}
                />
            )}
        </Box>
    );
};

export const AIChangeDetailPage = () => {
    const { id } = useParams();
    return (
        <AIChangesGate>
            <Box p={2}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <RouterLink to={BASE}>← AI changes</RouterLink>
                </Typography>
                <Disabled />
                <ChangeSetBody id={id} />
            </Box>
        </AIChangesGate>
    );
};

// ---- an MCP proposal's review page --------------------------------------------------------------------------------

const sentWord = (v) => (v === true ? 'Yes' : v === false ? 'No' : 'Not checked');

// A settings change's renders (backend `ai_edit.review._sent_diff`): would the notification be sent on this booking,
// before and after (`sent`; null: not checked), and why not (`not_sent_because`, the delivery check's own words).
const SentOnBooking = ({ notificationId, render }) => {
    const { sent } = render;
    if (!sent || typeof sent !== 'object') return null;
    const why = render.not_sent_because && typeof render.not_sent_because === 'object' ? render.not_sent_because : {};
    return (
        <Box>
            <Typography variant="body2" data-testid={`sent-${notificationId}-${render.booking}`}>
                {`Sent on this booking: ${sentWord(sent.before)} → ${sentWord(sent.after)}`}
            </Typography>
            {['before', 'after'].map((side) => sent[side] !== true && typeof why[side] === 'string' && why[side] && (
                <Typography key={side} variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>
                    {`${sent[side] === false ? 'Not sent' : 'Could not be checked'} ${side} the change: ${why[side]}`}
                </Typography>
            ))}
        </Box>
    );
};

const Renders = ({ proposalId }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [state, setState] = React.useState({ loading: false, data: null, error: null });
    const load = () => {
        setState({ loading: true, data: null, error: null });
        api.getProposalRenders(dataAPI, proposalId)
            .then((data) => setState({ loading: false, data, error: null }))
            .catch((error) => setState({ loading: false, data: null, error }));
    };
    // The page offers the renders on the review's `permissions.view_content` (an older backend: `view_content` "on some
    // operator"); the backend decides again on the proposal's operator (review W3 M1), and a refusal is final here.
    if (state.error?.code === 'all_tags_required') {
        return (
            <Alert severity="info">
                Real-booking renders of this change need the AI editing permission on all tags of its company: yours does not
                reach them there.
            </Alert>
        );
    }
    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Button variant="outlined" onClick={load} disabled={state.loading}>
                    {state.data ? 'Render again' : 'Show real-booking renders'}
                </Button>
                {state.loading && <CircularProgress size={20} />}
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                The change rendered on a few of your real bookings, before and after. Nothing is saved or sent.
            </Typography>
            {state.error && (
                <Alert
                    severity={state.error.code === 'busy' ? 'warning' : 'error'}
                    action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
                >
                    {state.error.code === 'busy' ? 'The renders are busy right now; try again.' : state.error.message}
                </Alert>
            )}
            {state.data && !state.data.ok && (
                <Alert severity="warning">
                    {`The change does not apply as proposed: ${typeof state.data.apply?.error === 'string' && state.data.apply.error
                        ? state.data.apply.error
                        : JSON.stringify(state.data.apply)}`}
                </Alert>
            )}
            {state.data && state.data.ok && state.data.notifications.length === 0 && !state.data.notifications_not_shown && (
                <Typography variant="body2">No notification is affected.</Typography>
            )}
            {state.data && franchiseNotShown(state.data.notifications_not_shown, 'rendered here') && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {franchiseNotShown(state.data.notifications_not_shown, 'rendered here')}
                </Typography>
            )}
            {state.data?.notifications?.map((n) => (
                <Paper key={n.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Typography variant="subtitle2">
                        <RouterLink to={`/notifications/${n.id}`}>{n.name}</RouterLink>{' '}
                        <Typography component="span" variant="caption" color="text.secondary">{n.event}</Typography>
                    </Typography>
                    {n.not_rendered_because && <Typography variant="caption" color="text.secondary">Not rendered: {n.not_rendered_because}</Typography>}
                    {n.renders.map((r) => (
                        <Box key={r.booking} sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.label}{r.language ? ` · ${r.language}` : ''}</Typography>
                            <SentOnBooking notificationId={n.id} render={r} />
                            {r.error && <Alert severity="error">{r.error}</Alert>}
                            {r.channels.map((c) => (
                                <Box key={`${c.delivery_method}-${c.channel}`} sx={{ mt: 0.5 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="caption">{c.channel} ({c.format})</Typography>
                                        <Chip size="small" label={c.changed ? 'Changed' : 'Unchanged'} color={c.changed ? 'warning' : 'default'} />
                                    </Stack>
                                    <Grid container spacing={1}>
                                        {['before', 'after'].map((side) => (
                                            <Grid item xs={12} md={6} key={side}>
                                                <Typography variant="caption" color="text.secondary">{side === 'before' ? 'Before' : 'After'}</Typography>
                                                {c.subject?.[side] && <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.subject[side]}</Typography>}
                                                <Box
                                                    component="pre"
                                                    data-testid={`render-${r.booking}-${c.delivery_method}-${side}`}
                                                    sx={{ m: 0, p: 1, bgcolor: 'grey.100', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}
                                                >
                                                    {c.error?.[side] ? `Error: ${c.error[side]}` : c.body?.[side] || '—'}
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Paper>
            ))}
        </Box>
    );
};

const ProposalBody = ({ id }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const perms = useAIPermissions();
    const [state, setState] = React.useState({ loading: true, p: null, error: null });
    const [reload, setReload] = React.useState(0);
    React.useEffect(() => {
        let alive = true;
        api.getProposal(dataAPI, id)
            .then((p) => alive && setState({ loading: false, p, error: null }))
            .catch((error) => alive && setState({ loading: false, p: null, error }));
        return () => {
            alive = false;
        };
    }, [dataAPI, id, reload]);
    if (state.loading && !state.p) return <CircularProgress size={24} />;
    if (state.error) {
        return <Alert severity={state.error.status === 404 ? 'warning' : 'error'}>{state.error.status === 404 ? notFoundText('proposal', perms) : state.error.message}</Alert>;
    }
    const p = state.p;
    return (
        <Box>
            <PageMeta title={`AI proposal #${p.id}`} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Proposed change #{p.id}</Typography>
                <StatusChip status={p.status} />
            </Stack>
            <ReasonText text={p.request.reason} />
            <Grid container spacing={2} sx={{ my: 1 }}>
                <Info label="Asked by">{actorLabel(p.request.requested_by, p.request)}</Info>
                <Info label="Client">
                    {(p.request.oauth_client_name || p.request.oauth_client_id)
                        ? <ClientName name={p.request.oauth_client_name} id={p.request.oauth_client_id} />
                        : null}
                </Info>
                <Info label="Created">{fmtTs(p.created_ts)}</Info>
                <Info label="Previewed">{p.request.preview_ready ? fmtTs(p.request.preview_ts) : 'Not yet'}</Info>
            </Grid>
            <ProposalItems title="Items" items={p.proposal.items} />
            <PreviewSummary summary={p.request.preview_summary} />
            <FranchiseCopySummary franchise={p.request.preview_summary?.franchise} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">Requests to apply</Typography>
            {p.pending_changes.length === 0 && <Typography variant="body2" color="text.secondary">None.</Typography>}
            {/* what each request does is the preview above: not again on its card */}
            {p.pending_changes.map((pc) => (
                <PendingChangeCard key={pc.id} pending={pc} showSummary={false} onDecided={() => setReload((n) => n + 1)} />
            ))}
            {p.change_sets.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Applied as</Typography>
                    {p.change_sets.map((c) => (
                        <Typography key={c.id} variant="body2"><ChangeSetLink id={c.id}>{`change set ${c.id.slice(0, 8)}`}</ChangeSetLink> · {c.status}</Typography>
                    ))}
                </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">On real bookings</Typography>
            {/* decided on the proposal's operator (ORCH W4 b); an older backend: "on some operator" */}
            {(typeof p.permissions?.view_content === 'boolean' ? p.permissions.view_content : perms?.view_content) ? (
                <Renders proposalId={p.id} />
            ) : (
                <Typography variant="body2" color="text.secondary">Real-booking renders need the AI editing permission on all tags.</Typography>
            )}
        </Box>
    );
};

export const AIProposalReviewPage = () => {
    const { id } = useParams();
    return (
        <AIChangesGate>
            <Box p={2}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <RouterLink to={BASE}>← Approvals</RouterLink>
                </Typography>
                <Disabled />
                <ProposalBody id={id} />
            </Box>
        </AIChangesGate>
    );
};
